import React from 'react';
import { Box, Button, DataTable } from '../../../../common/components';
import {
  applyFilter,
  refreshData,
} from '../../../../common/components/DataTable/redux/dataTableActions';
import { TableHeaderColumn } from 'react-bootstrap-table';
import { Row, Col, ControlLabel, FormControl, FormGroup } from 'react-bootstrap';
import {
  addToPrintJobs,
  generateDocuments,
  removeFromPrintJobs,
  sendCancellationEmail,
  sendCustomerLoginEmail,
  sendCustomerResetPasswordEmail,
} from '../../redux/policyActions';
import getIncludedResource from '../../../../helpers/getIncludedResource';
import { InternalButtonBar } from '../../../layout/components';
import DownloadDocumentsButton from '../DownloadDocumentsButton/DownloadDocumentsButton';
import formatDateTime from '../../../../helpers/formatDateTime';
import { connect } from 'react-redux';

const PA_PRODUCTS = [
  'Hospital Cash Plan',
  'Cancer Care',
  'Personal Accident',
  'Surgery Cash Plan',
];

const PolicyDocuments = ({
  resource,
  reference,
  isGeneratingDocs,
  documents,
  sendCustomerResetPasswordEmail,
  applyFilter,
  refreshData,
  generateDocuments,
  removeFromPrintJobs,
  addToPrintJobs,
  sendCancellationEmail,
}) => {
  if (!resource.data?.id) {
    return null;
  }

  const productName = resource.data.attributes.product_name;
  const isPaProduct = PA_PRODUCTS.includes(productName);

  const handleCustomerResetPasswordEmail = () => {
    sendCustomerResetPasswordEmail(
      resource.data.id,
      resource.data.attributes.policy_number ? 'policies' : 'policies/applications',
    );
  };

  const handleFilterUpdate = (event) => {
    applyFilter('documents', 'filter[version]', event.target.value);
  };

  const handleRowClick = (row) => {
    if (window) {
      window.open(row.file_path, '_blank');
    }

    return false;
  };

  const handleGenerateDocuments = () => {
    generateDocuments(
      resource,
      resource.data.attributes.policy_number ? 'policies' : 'policies/applications',
      () => refreshData('documents'),
    );
  };

  const handleTogglePrintJobs = () => {
    const onPrintQueue = !!resource.data.attributes.metadata.in_print_queue;
    const isPolicy = resource.data.type === 'policies';
    const isMta = isPolicy && resource.data.attributes.version > 1;
    const isRenewal = !isPolicy && resource.data.attributes.quote_type === 'renewal';
    const resourcePath = isPolicy ? 'policies' : 'policies/applications';
    const quoteQueue = isRenewal ? 'renewal' : '';
    const queue = isMta ? 'mta' : quoteQueue;
    const func = onPrintQueue ? removeFromPrintJobs : addToPrintJobs;
    func(resource, resourcePath, queue, () => refreshData('documents'));
  };

  const handleCancellationEmail = () => {
    sendCancellationEmail(resource.data.id);
  };

  const buttons = () => {
    const disableDownloadButton = !documents || documents.data.length === 0;
    const type = resource.data.attributes.policy_number ? 'policy' : 'application';
    const customer = getIncludedResource(resource.data, resource.included, 'customer');
    const onPrintQueue = !!resource.data.attributes.metadata.in_print_queue;
    const address = customer.data?.attributes?.address;

    const canTogglePrintQueue = address?.line1 && address?.postcode && !isPaProduct;

    let buttons = [
      {
        orgTypes: ['owner', 'broker'],
        button: (
          <Button
            bsStyle="primary"
            className="pull-right"
            label="Generate New Documents"
            rightIcon="file-o"
            isLoading={isGeneratingDocs}
            handleClick={handleGenerateDocuments}
          />
        ),
      },
      {
        orgTypes: ['owner', 'broker'],
        button: (
          <DownloadDocumentsButton
            label="Download Documents"
            className="pull-right"
            resourceId={resource.data.id}
            customer={customer}
            isLoading={isGeneratingDocs}
            documents={documents ? documents.data : []}
            disabled={disableDownloadButton}
            resourceType={
              resource.data.attributes.policy_number
                ? 'policies'
                : 'policies/applications'
            }
          />
        ),
      },
    ];

    if (canTogglePrintQueue) {
      buttons.push({
        orgTypes: ['owner'],
        button: (
          <Button
            bsStyle="primary"
            className="pull-right"
            label={(onPrintQueue ? 'Remove from' : 'Add to') + ' Print Job'}
            rightIcon="print"
            isLoading={isGeneratingDocs}
            handleClick={handleTogglePrintJobs}
          />
        ),
      });
    }

    if (resource.data.attributes.status === 'cancelled') {
      buttons.push({
        orgTypes: ['owner', 'broker'],
        button: (
          <Button
            label="Send Policy Cancellation Email"
            bsStyle="primary"
            className="pull-right pad-right"
            handleClick={handleCancellationEmail}
          />
        ),
      });
    }

    if (type === 'policy' || resource.data.attributes.status !== 'applied') {
      buttons.push({
        orgTypes: ['owner', 'broker'],
        button: (
          <Button
            label="Send Reset Password Email"
            bsStyle="primary"
            className="pull-right pad-right"
            handleClick={handleCustomerResetPasswordEmail}
          />
        ),
      });
    }

    return buttons;
  };

  return (
    <Box>
      <h2 className="resource-name">{reference}</h2>
      <div className="row">
        <div className="col-sm-4 col-sm-offset-8">
          <FormGroup controlId="select-status">
            <Col componentClass={ControlLabel} sm={4}>
              Versions
            </Col>
            <Col sm={8}>
              <FormControl
                componentClass="select"
                type="select"
                onChange={handleFilterUpdate}
                placeholder="select"
              >
                <option value="">Current</option>
                <option value="all">All</option>
              </FormControl>
            </Col>
          </FormGroup>
        </div>
      </div>

      <div className="hr transparent" />

      {/* We don't want to send through anything more than 1 if this value is populated */}
      <DataTable
        waitForItems={resource?.data.attributes?.minimum_document_count > 0 ? 1 : 0}
        waitForItemsMessage="Please wait whilst we get your documents. If you have just issued a policy or saved a quote, then your documents are being generated in the background and could take up to a minute to show here."
        source="documents"
        autoFilters={[
          {
            type:
              'filter[' +
              (resource.data.attributes.policy_number ? 'policy' : 'policy_application') +
              ']',
            value: resource.data.id,
          },
        ]}
        name="documents"
        showTotal
        onRowSelect={handleRowClick}
      >
        <TableHeaderColumn dataField="id" isKey hidden />

        <TableHeaderColumn
          dataField={`${isPaProduct ? 'display_name' : 'name'}`}
          dataSort
        >
          Name
        </TableHeaderColumn>

        <TableHeaderColumn dataField="version_number" dataSort width={'100px'}>
          Version
        </TableHeaderColumn>

        <TableHeaderColumn
          dataField="from_date"
          dataFormat={formatDateTime}
          dataSort
          width={'180px'}
        >
          Effective Date
        </TableHeaderColumn>

        <TableHeaderColumn
          dataField="created_at"
          dataFormat={formatDateTime}
          dataSort
          width={'180px'}
        >
          Created
        </TableHeaderColumn>
      </DataTable>

      <Row>
        <Col xs={12}>
          <InternalButtonBar buttons={buttons()} />
        </Col>
      </Row>
    </Box>
  );
};

export default connect(
  (state) => ({
    documents: state.dataTables.tables.documents,
  }),
  {
    sendCustomerResetPasswordEmail: sendCustomerResetPasswordEmail,
    sendCustomerLoginEmail: sendCustomerLoginEmail,
    applyFilter: applyFilter,
    refreshData: refreshData,
    generateDocuments: generateDocuments,
    removeFromPrintJobs: removeFromPrintJobs,
    addToPrintJobs: addToPrintJobs,
    sendCancellationEmail: sendCancellationEmail,
  },
)(PolicyDocuments);
